import React, { useState, useEffect, useContext } from 'react';
import Chart from "react-apexcharts";
import { apiContext } from '../../../Api';

// Reusable function to generate dynamic HSL colors using Golden Ratio distribution
const generateDynamicColors = (count) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
        const hue = Math.floor((i * 137.508) % 360);
        // Slightly softer saturation for a cleaner, modern pastel-ish look
        colors.push(`hsl(${hue}, 75%, 52%)`);
    }
    return colors;
};

export default function PieChartPublikasi() {
    const { axios } = useContext(apiContext);
    const [chartData, setChartData] = useState({ series: [], labels: [], colors: [], rawCounts: [], total: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAndProcessData = async () => {
            try {
                const response = await axios.get('/data');
                const publikasiData = response.data || [];

                const typeCounts = {};

                publikasiData.forEach(item => {
                    const typeName = item?.jenis_publikasi?.nama || 'Tidak Terdefinisi';
                    const cleanTypeName = String(typeName).trim();
                    typeCounts[cleanTypeName] = (typeCounts[cleanTypeName] || 0) + 1;
                });

                const labels = Object.keys(typeCounts);
                const rawCounts = Object.values(typeCounts);
                const total = publikasiData.length;

                const series = rawCounts.map(count =>
                    total > 0 ? Number(((count / total) * 100).toFixed(1)) : 0
                );

                const colors = generateDynamicColors(labels.length);

                setChartData({ series, labels, colors, rawCounts, total });
            } catch (error) {
                console.error("Gagal mengambil data pie chart publikasi:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAndProcessData();
    }, [axios]);

    const hasData = chartData.series.length > 0;

    const options = {
        chart: {
            type: 'polarArea',
            fontFamily: 'Inter, sans-serif',
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                dynamicAnimation: {
                    enabled: true,
                    speed: 350
                }
            }
        },
        labels: hasData ? chartData.labels : ['Belum ada data'],
        colors: hasData ? chartData.colors : ['#cbd5e1'],
        stroke: {
            colors: ['#ffffff'],
            width: 2
        },
        fill: {
            opacity: 0.9
        },
        yaxis: {
            min: 0,
            max: 100,
            show: false,
        },
        plotOptions: {
            polarArea: {
                rings: {
                    strokeWidth: 1,
                    strokeColor: '#f1f5f9',
                },
                spokes: {
                    strokeWidth: 1,
                    connectorColors: '#f1f5f9',
                }
            }
        },
        dataLabels: {
            enabled: hasData,
            formatter: function (val, opts) {
                // Keep the label clean, just showing the count and percent
                const count = chartData.rawCounts[opts.seriesIndex];
                return `${count} (${Math.round(val)}%)`;
            },
            style: {
                fontSize: '11px',
                fontWeight: 600,
                colors: ['#0f172a']
            },
            background: {
                enabled: true,
                foreColor: '#ffffff',
                padding: 4,
                borderRadius: 4,
                borderWidth: 0,
                opacity: 0.9,
                dropShadow: {
                    enabled: true,
                    top: 1,
                    left: 1,
                    blur: 2,
                    color: '#000',
                    opacity: 0.15
                }
            },
            dropShadow: {
                enabled: false,
            }
        },
        legend: {
            show: hasData,
            position: 'bottom',
            fontWeight: 500,
            fontSize: '13px',
            itemMargin: {
                horizontal: 10,
                vertical: 6
            },
            markers: {
                radius: 12,
                width: 12,
                height: 12
            }
        },
        states: {
            hover: {
                filter: {
                    type: 'darken',
                    value: 0.9
                }
            },
            active: {
                allowMultipleDataPointsSelection: false,
                filter: {
                    type: 'darken',
                    value: 0.8
                }
            }
        },
        tooltip: {
            enabled: hasData,
            theme: 'light',
            style: {
                fontSize: '12px',
                color: '#0f172a'
            },
            y: {
                formatter: function (val, opts) {
                    const count = chartData.rawCounts[opts.seriesIndex];
                    return `${count} Publikasi (${val}%)`;
                }
            }
        },
        responsive: [{
            breakpoint: 480,
            options: {
                chart: {
                    height: 320
                },
                legend: {
                    position: 'bottom'
                }
            }
        }]
    };

    const seriesToRender = hasData ? chartData.series : [100];

    return (
        <div className="flex h-full w-full flex-col justify-center transition-all duration-300">
            <h3 className="mb-4 text-center text-sm font-extrabold text-slate-700 uppercase tracking-wider">
                Distribusi Jenis Publikasi
            </h3>

            {loading ? (
                <div className="flex h-[320px] items-center justify-center">
                    <span className="text-sm font-medium text-slate-400 animate-pulse">Memuat data grafik...</span>
                </div>
            ) : (
                <div className="relative w-full flex justify-center items-start pt-2">
                    {hasData && (
                        <div className="absolute top-[22px] w-[265px] h-[265px] rounded-full pointer-events-none"></div>
                    )}

                    <div className="w-full z-10 relative">
                        <Chart
                            options={options}
                            series={seriesToRender}
                            type="polarArea"
                            height={340}
                            width="100%"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
